import { useTranslate } from 'module/hooks/use-translation';
import { PageTemplateSectionSource } from 'api/custom-section/types';
import {
  AveragePerCharacterWidth,
  listIntoSentence,
} from 'helpers/list-into-sentence';
import { uniqBy } from 'lodash';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SharedPreviewConfiguration } from '../../../shared/preview-configuration';
import {
  SectionDescription,
  SectionPreview,
  SectionTitle,
} from '../../../shared/section-styles';
import { CompanyPageSourceDerivedSectionComponentProps } from '../types';

const useSourceIntoText = (sections: PageTemplateSectionSource[]) => {
  const translate = useTranslate();
  const uniqueSections = useMemo(
    () => uniqBy(sections, 'pageTemplateId'),
    [sections],
  );
  const [sourceToDisplay, setSourceToDisplay] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (wrapperRef.current) {
      const wrapperWidth = wrapperRef.current.offsetWidth;
      const availableWidth = wrapperWidth * 0.6;
      const maxChars = Math.floor(availableWidth / AveragePerCharacterWidth);
      const sources = uniqueSections.map(
        ({ pageTemplateName }) => pageTemplateName,
      );

      setSourceToDisplay(
        listIntoSentence({
          list: sources ?? [],
          separator: ', ',
          andSeparator: ` ${translate('and')} `,
          maximumNumberOfCharactersAllowed: maxChars,
          moreConstructor: (count) =>
            translate(
              'rich_text_editor.sections.company_page_source_derived_section.sources.label.more_sources',
              { count },
            ),
        }),
      );
    }
  }, [translate, uniqueSections]);

  return {
    sourceToDisplay,
    wrapperRef,
  };
};

interface Props extends CompanyPageSourceDerivedSectionComponentProps {
  onWrapperClick: () => void;
}
export const CompanyPageSourceDerivedSectionPromptPreview: FC<Props> = ({
  node,
  onWrapperClick,
}) => {
  const configurationBarValue = {
    groupBy: node.attrs.config?.groupBy,
    generateAs: node.attrs.config?.generateAs,
  };
  const { sourceToDisplay, wrapperRef } = useSourceIntoText(
    node.attrs.config?.sourcePageTemplateSections ?? [],
  );

  return (
    <SectionPreview
      onClick={(event) => {
        event.stopPropagation();
        onWrapperClick();
      }}
    >
      <div>
        <SectionTitle>{node.attrs.title}</SectionTitle>
        <SectionDescription ref={wrapperRef}>
          <FormattedMessage
            id="rich_text_editor.sections.company_page_source_derived_section.sources.label"
            values={{
              sources: sourceToDisplay,
            }}
          />
        </SectionDescription>
      </div>
      <SharedPreviewConfiguration prompt={configurationBarValue} />
    </SectionPreview>
  );
};
